import {AxiosInstance} from 'axios';

export function createPendingDocsApi(client: AxiosInstance) {
    return {
        async listPendingDocs(status?: string) {
            const params: any = {};
            if (status) params.status = status;
            const response = await client.get('/api/pending-docs', {params});
            return response.data;
        },
        async getPendingDoc(id: string) {
            const response = await client.get(`/api/pending-docs/${id}`);
            return response.data;
        },
        async getPendingDocStatus(id: string) {
            const response = await client.get(`/api/pending-docs/${id}/status`);
            return response.data;
        },
        async uploadPdf(file: File): Promise<any> {
            const formData = new FormData();
            formData.append('file', file);
            const response = await client.post('/api/pending-docs/upload', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            });
            return response.data;
        },
        async importZip(file: File): Promise<any> {
            const formData = new FormData();
            formData.append('file', file);
            const response = await client.post('/api/pending-docs/import', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            });
            return response.data;
        },
        async deletePendingDoc(id: string, clearCache: boolean = false) {
            const params = clearCache ? {clear_cache: true} : {};
            const response = await client.delete(`/api/pending-docs/${id}`, {params});
            return response.data;
        },
        async retryExtraction(id: string) {
            const response = await client.post(`/api/pending-docs/${id}/retry`);
            return response.data;
        },
        async getPendingDocChapters(id: string) {
            const response = await client.get(`/api/pending-docs/${id}/chapters`);
            return response.data;
        },
        async updatePendingDocChapter(docId: string, chapterIndex: number, data: {markdown?: string; title?: string}) {
            const response = await client.put(`/api/pending-docs/${docId}/chapters/${chapterIndex}`, data);
            return response.data;
        },
        async addPendingDocChapter(docId: string, data: {title: string; initial_markdown?: string; insert_after: number}) {
            const response = await client.post(`/api/pending-docs/${docId}/chapters`, data);
            return response.data;
        },
        async deletePendingDocChapter(docId: string, chapterIndex: number) {
            const response = await client.delete(`/api/pending-docs/${docId}/chapters/${chapterIndex}`);
            return response.data;
        },
        async getPendingDocImages(id: string, status?: string) {
            const params: any = {};
            if (status) params.status = status;
            const response = await client.get(`/api/pending-docs/${id}/images`, {params});
            return response.data;
        },
        async uploadImage(docId: string, file: File, chapterIndex?: number): Promise<any> {
            const formData = new FormData();
            formData.append('file', file);
            if (chapterIndex !== undefined) {
                formData.append('chapter_index', String(chapterIndex));
            }
            const response = await client.post(`/api/pending-docs/${docId}/images`, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            });
            return response.data;
        },
        async deleteImage(docId: string, imageId: number) {
            const response = await client.delete(`/api/pending-docs/${docId}/images/${imageId}`);
            return response.data;
        },
        async updateImage(docId: string, imageId: number, updates: {filename?: string; chapter_index?: number | null}) {
            const response = await client.put(`/api/pending-docs/${docId}/images/${imageId}`, updates);
            return response.data;
        },
        async validatePendingDoc(id: string) {
            const response = await client.get(`/api/pending-docs/${id}/validate`);
            return response.data;
        },
        async getPendingDocConfig(id: string) {
            const response = await client.get(`/api/pending-docs/${id}/config`);
            return response.data;
        },
        async updatePendingDocConfig(id: string, config: {
            doc_id?: string; title?: string; doc_type?: 'regulation' | 'guidance';
            publisher?: string; source_url?: string;
            scope_level?: 'national' | 'kommune' | 'bydel';
            scope_location?: Record<string, string>;
        }) {
            const response = await client.put(`/api/pending-docs/${id}/config`, config);
            return response.data;
        },
        async previewPendingDocSections(id: string) {
            const response = await client.get(`/api/pending-docs/${id}/preview-sections`);
            return response.data;
        },
        async checkDocIdExists(docId: string) {
            const response = await client.get(`/api/pending-docs/docs/exists/${encodeURIComponent(docId)}`);
            return response.data;
        },
        /** Stage 7.2 — the server consults validation and 422s on a failed
         *  verdict. `overrideValidation` records that a human accepted the known
         *  problems; a document with no chapters is refused regardless. */
        async approvePendingDoc(id: string, overrideValidation = false) {
            const response = await client.post(
                `/api/pending-docs/${id}/approve`,
                null,
                {params: {override_validation: overrideValidation}},
            );
            return response.data;
        },
        async getKommuneList(): Promise<{code: string; name: string}[]> {
            const response = await client.get('/api/pending-docs/kommune');
            return response.data;
        },
        async getPublisherList(): Promise<string[]> {
            const response = await client.get('/api/pending-docs/publishers');
            return response.data.publishers;
        },

        // Web Sources API
        async getWebSources(): Promise<{sources: Array<{key: string; name: string; description: string}>}> {
            const response = await client.get('/api/web-sources');
            return response.data;
        },
        async fetchWebSource(sourceKey: string, force: boolean = false, url?: string, docId?: string): Promise<{
            pending_doc_id: string; job_id: string; cached: boolean;
            // One in-flight document per web source (SB-119): when set, pending_doc_id
            // is the EXISTING document and no new extraction was started.
            is_duplicate?: boolean; existing_status?: string | null; warning?: string | null;
            superseded_count?: number;
        }> {
            // lovdata is URL-driven: pass the lovdata URL + a short doc id (e.g. VEGLOVEN).
            // force=true fetches even if a document is in flight (it is superseded).
            const params: Record<string, unknown> = {force};
            if (url) params.url = url;
            if (docId) params.doc_id = docId;
            const response = await client.post(`/api/web-sources/${sourceKey}/fetch`, null, {params});
            return response.data;
        },
    };
}
